import { type FC } from "react";
import { Avatar, Box, Card, Grid, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import type { UsersItem } from "@/partner/entities/users";
import { getInitials } from "@/partner/shared/utils";
import { ItemTitleLink } from "@/partner/shared/components/ItemTitleLink";
import { PagePaths } from "@/partner/shared/constants";
import type { UserType } from "@/types/graphql/partner.generated";

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  "&:hover": {
    textDecoration: "none",
  },
}));

export interface UserListItemProps {
  user: UsersItem;
  onClickItem: (id: string) => void;
  isMobile?: boolean;
}

export const UserListItem: FC<UserListItemProps> = ({
  user,
  onClickItem,
  isMobile,
}) => {
  const { t } = useTranslation();
  const displayName = user.displayName || user.email || t("users.item.noName");

  return (
    <StyledCard elevation={0}>
      <Grid container spacing={2} wrap="nowrap" alignItems="center">
        <Grid size="auto">
          <Avatar
            src={user.avatar?.url ?? undefined}
            sx={{ width: 40, height: 40, bgcolor: "primary.main" }}
          >
            {getInitials(user as UserType)}
          </Avatar>
        </Grid>

        <Grid size="grow" sx={{ minWidth: 0 }}>
          <ItemTitleLink
            to={`${PagePaths.users}?tab=details&id=${user.id}`}
            onOpen={() => onClickItem(user.id)}
            underline="hover"
            variant="h6"
            color="textPrimary"
            noWrap
            sx={{ display: "block" }}
          >
            {displayName}
          </ItemTitleLink>
          {user.email && (
            <Typography variant="body2" color="text.secondary" noWrap>
              {user.email}
            </Typography>
          )}
        </Grid>

        {!isMobile && (
          <Grid size="auto">
            <Box>
              <Typography variant="body2" color="textPrimary" align="right">
                {t("users.item.registeredAt", { time: user.createdAt })}
              </Typography>
              <Typography variant="body2" color="textSecondary" align="right">
                {t("users.item.registeredDate", { time: user.createdAt })}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </StyledCard>
  );
};
